"use client";

import { useAuthRequiredModal } from "@/components/auth/auth-required-modal";
import { useProUser } from "@/contexts/pro-user-context";
import { useUser } from "@/hooks/useUser";
import {
  getPlusPlansByInterval,
  getProPlansByInterval,
  getSubscriptionPlanById,
} from "@/lib/constants/pricing";
import { createCheckoutSession } from "@/lib/payments";
import { cn } from "@/lib/utils";
import { track } from "@masterkey/analytics/client";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { LoadingDots } from "../loading-dots";
import { SubscriptionPlansModal } from "../subscription-plans-modal";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";

interface PremiumUpgradeBannerProps {
  className?: string;
}

export function PremiumUpgradeBanner({ className }: PremiumUpgradeBannerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isYearly, setIsYearly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<"plus" | "pro">("pro");
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const { toast } = useToast();
  const { requireAuth, AuthRequiredModal } = useAuthRequiredModal();
  const { isPaid } = useProUser();

  if (isPaid) {
    return null;
  }

  const handleShowFeatures = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleCheckout = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!user) {
        try {
          await requireAuth();
        } catch {
          setIsLoading(false);
          return;
        }
      }

      track({ event: "upgrade-intent", source: "premium-upgrade-banner" });

      const planId =
        `${selectedPlan}_${isYearly ? "yearly" : "monthly"}` as const;
      const plan = getSubscriptionPlanById(planId);

      await createCheckoutSession({
        priceId: plan.priceId,
        plan: planId,
        isTrial: false,
      });

      return;
    } catch (error) {
      console.error("Error during checkout:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  }, [user, isYearly, selectedPlan, requireAuth, toast]);

  const handleToggleYearly = useCallback(() => {
    setIsYearly(!isYearly);
  }, [isYearly]);

  const plusPlans = getPlusPlansByInterval();
  const proPlans = getProPlansByInterval();
  const currentPlusPrice = isYearly ? plusPlans[1] : plusPlans[0];
  const currentProPrice = isYearly ? proPlans[1] : proPlans[0];
  const currentPrice =
    selectedPlan === "plus" ? currentPlusPrice : currentProPrice;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "relative overflow-hidden rounded-xl bg-white border border-gray-200 shadow-lg",
          className,
        )}
      >
        {/* Decorative elements */}
        <div className="absolute right-0 top-0 h-48 w-48 translate-x-12 -translate-y-12 bg-canary/5 rounded-full blur-3xl" />
        <div className="absolute left-0 bottom-0 h-48 w-48 -translate-x-12 translate-y-12 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="relative px-6 py-8 md:p-8">
          {/* Header Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center rounded-full bg-canary/10 px-3 py-1 mb-4">
              <span className="text-sm font-semibold text-smokeyBlack">
                Unlock Premium Features
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Supercharge Your Property Analysis
            </h3>
            <p className="text-gray-600 max-w-xl mx-auto">
              Get unlimited AI-powered insights and analysis to make confident
              decisions about any property you're interested in.
            </p>
          </div>

          {/* Main Content Container */}
          <div className="sm:grid sm:grid-cols-2 sm:gap-8 max-w-3xl mx-auto">
            {/* Left Column - Plan Selection & Details */}
            <div>
              {/* Plan Selection */}
              <div className="bg-chalk rounded-2xl p-1.5 flex gap-1.5 mb-6">
                <button
                  onClick={() => setSelectedPlan("plus")}
                  className={cn(
                    "flex-1 rounded-xl py-4 text-sm transition-all duration-200 relative",
                    selectedPlan === "plus"
                      ? "bg-white shadow-sm"
                      : "hover:bg-white/50",
                  )}
                  type="button"
                >
                  <div className="font-semibold text-smokeyBlack mb-1">
                    Plus
                  </div>
                  <div className="text-xs text-gray-600">10 analyses/mo</div>
                </button>
                <button
                  onClick={() => setSelectedPlan("pro")}
                  className={cn(
                    "flex-1 rounded-xl py-4 text-sm transition-all duration-200 relative",
                    selectedPlan === "pro"
                      ? "bg-white shadow-sm"
                      : "hover:bg-white/50",
                  )}
                  type="button"
                >
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 text-xs bg-smokeyBlack text-white rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                  <div className="font-semibold text-smokeyBlack mb-1">Pro</div>
                  <div className="text-xs text-gray-600">Unlimited</div>
                </button>
              </div>

              {/* Plan Details Card */}
              <div className="bg-chalk/50 rounded-2xl p-6">
                <div
                  className={cn(
                    "text-sm transition-all duration-200",
                    selectedPlan === "plus" ? "block" : "hidden",
                  )}
                >
                  <div className="font-medium text-gray-900 mb-3">
                    Perfect for occasional buyers
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Up to 10 AI analyses per month
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Limited daily AI chat
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Basic document tools
                    </li>
                  </ul>
                </div>
                <div
                  className={cn(
                    "text-sm transition-all duration-200",
                    selectedPlan === "pro" ? "block" : "hidden",
                  )}
                >
                  <div className="font-medium text-gray-900 mb-3">
                    Best for serious buyers
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Unlimited AI analysis
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Unlimited AI chat
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-gray-600" />
                      Advanced tools & insights
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column - Pricing & CTA */}
            <div className="mt-6 sm:mt-0">
              {/* Billing Section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
                {/* Billing Toggle */}
                <div className="flex justify-center mb-6">
                  <div className="inline-flex items-center gap-3">
                    <button
                      onClick={() => setIsYearly(false)}
                      className={cn(
                        "text-sm transition-colors",
                        !isYearly
                          ? "font-semibold text-smokeyBlack"
                          : "text-gray-500",
                      )}
                      type="button"
                    >
                      Monthly
                    </button>
                    <div
                      className="h-6 w-11 rounded-full bg-gray-200 cursor-pointer relative"
                      onClick={handleToggleYearly}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleToggleYearly();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <motion.div
                        className="absolute top-1 left-1 h-4 w-4 rounded-full bg-canary"
                        initial={false}
                        animate={{
                          x: isYearly ? 20 : 0,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 30,
                        }}
                      />
                    </div>
                    <button
                      onClick={() => setIsYearly(true)}
                      className={cn(
                        "text-sm transition-colors",
                        isYearly
                          ? "font-semibold text-smokeyBlack"
                          : "text-gray-500",
                      )}
                      type="button"
                    >
                      Yearly
                    </button>
                  </div>
                </div>

                {/* Price Display */}
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900">
                    ${currentPrice.monthlyPrice}
                    <span className="text-xl font-normal text-gray-500">
                      /mo
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {isYearly ? (
                      <span className="inline-flex flex-col items-center gap-1">
                        <span>Billed yearly</span>
                        <span className="text-green-600 font-medium">
                          Save 17% ($60/year)
                        </span>
                      </span>
                    ) : (
                      "Billed monthly"
                    )}
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  size="lg"
                  className={cn(
                    "w-full bg-smokeyBlack hover:bg-smokeyBlack/90 text-white font-semibold text-base px-8 group h-12",
                    isLoading && "cursor-not-allowed opacity-80",
                  )}
                  onClick={handleCheckout}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <LoadingDots />
                  ) : (
                    <>
                      Upgrade to {selectedPlan === "plus" ? "Plus" : "Pro"}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              </div>

              {/* Additional Features Link */}
              <div className="text-center space-y-4">
                <button
                  onClick={handleShowFeatures}
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  See all premium features
                </button>

                {/* Trust Signals */}
                <div className="text-xs text-gray-500">
                  Cancel anytime • 30-day money-back guarantee
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <SubscriptionPlansModal isOpen={isModalOpen} onClose={handleModalClose} />

      <AuthRequiredModal
        title="Sign in to upgrade"
        description="Sign in to unlock premium features and AI-powered tools"
      />
    </>
  );
}
